import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend, RadialBarChart, RadialBar } from "recharts";
import { TrendingUp, DollarSign, Timer, Users, RefreshCw, Filter, Sparkles, Target, Heart } from "lucide-react";
import { GapReportBody } from "./SkillCenter";

/**
 * Phase 73 · R4 + R5 — Reports Hub
 *
 * Five pre-built dashboards + full-funnel visualizer in a tabbed shell:
 *   • Executive KPIs (default)
 *   • By recruiter
 *   • By source (channel ROI)
 *   • By job
 *   • By client
 *   • Funnel (R5)
 *
 * All views respect the same global period filter at the top of the page.
 */
const TABS = [
    { id: "executive", label: "Executive" },
    { id: "hire_eta",  label: "Hire ETA" },
    { id: "qtth",      label: "qTTH" },
    { id: "client_health", label: "Client Health" },
    { id: "recruiter", label: "By Recruiter" },
    { id: "source",    label: "By Source" },
    { id: "job",       label: "By Job" },
    { id: "client",    label: "By Client" },
    { id: "funnel",    label: "Funnel" },
    { id: "skill_gap", label: "Skill Gap" },
];
const PIE_COLORS = ["#003fbf", "#059669", "#d97706", "#dc2626", "#7c3aed", "#0891b2", "#4b5563"];

export default function ReportsHub() {
    const [tab, setTab] = useState("executive");
    const [period, setPeriod] = useState({ from: "", to: "" });

    return (
        <div className="max-w-7xl mx-auto p-3 sm:p-6 lg:p-8" data-testid="reports-hub">
            <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb">Reporting</div>
                    <h1 className="font-display font-black text-2xl sm:text-4xl mt-1">Results &amp; ROI</h1>
                </div>
                <PeriodPicker period={period} setPeriod={setPeriod} />
            </div>

            <div className="border-b border-border flex flex-wrap gap-0.5 mb-5">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        data-testid={`reports-tab-${t.id}`}
                        className={`px-3 py-2 text-xs font-mono uppercase tracking-widest border-b-2 -mb-px transition-colors ${
                            tab === t.id ? "border-ikb text-ikb" : "border-transparent text-muted-foreground hover:text-foreground"
                        }`}
                    >{t.label}</button>
                ))}
            </div>

            {tab === "executive" && <ExecutiveTab period={period} />}
            {tab === "hire_eta" && <HireEtaTab />}
            {tab === "qtth" && <QtthTab />}
            {tab === "client_health" && <ClientHealthTab />}
            {tab === "recruiter" && <TableTab period={period} url="/reports/by-recruiter" testid="recruiter" columns={[
                { key: "name", label: "Recruiter" }, { key: "hires", label: "Hires", align: "right" },
                { key: "avg_tth_days", label: "Avg TTH (days)", align: "right" },
                { key: "revenue", label: "Revenue", align: "right", format: (v) => `$${(v || 0).toLocaleString()}` },
                { key: "no_show", label: "No-shows", align: "right" },
            ]} />}
            {tab === "source" && <SourceTab period={period} />}
            {tab === "job" && <TableTab period={period} url="/reports/by-job" testid="job" columns={[
                { key: "title", label: "Job" }, { key: "hires", label: "Hires", align: "right" },
                { key: "avg_tth_days", label: "Avg TTH (days)", align: "right" },
                { key: "cost", label: "Cost", align: "right", format: (v) => `$${(v || 0).toLocaleString()}` },
                { key: "revenue", label: "Revenue", align: "right", format: (v) => `$${(v || 0).toLocaleString()}` },
            ]} />}
            {tab === "client" && <TableTab period={period} url="/reports/by-client" testid="client" columns={[
                { key: "name", label: "Client" }, { key: "hires", label: "Hires", align: "right" },
                { key: "cost", label: "Cost", align: "right", format: (v) => `$${(v || 0).toLocaleString()}` },
                { key: "revenue", label: "Revenue", align: "right", format: (v) => `$${(v || 0).toLocaleString()}` },
                { key: "net", label: "Net", align: "right", format: (v) => `$${(v || 0).toLocaleString()}` },
            ]} />}
            {tab === "funnel" && <FunnelTab period={period} />}
            {tab === "skill_gap" && <SkillGapTab />}
        </div>
    );
}

function SkillGapTab() {
    const [industries, setIndustries] = useState([]);
    const [companyIndustries, setCompanyIndustries] = useState([]);
    const [industry, setIndustry] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [inds, ci] = await Promise.all([
                    api.get("/skill-center/industries"),
                    api.get("/company/industries"),
                ]);
                const list = inds.data.items || [];
                const owned = ci.data.industries || [];
                setIndustries(list);
                setCompanyIndustries(owned);
                setIndustry(owned[0] || list[0]?.slug || "");
            } catch (err) { toast.error(formatApiError(err)); }
        })();
    }, []);

    const load = useCallback(async () => {
        if (!industry) return;
        setLoading(true);
        try {
            const { data } = await api.get("/skill-center/gap-report", { params: { industry } });
            setData(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [industry]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-4" data-testid="reports-skill-gap">
            <div className="border border-border bg-white p-3 flex items-center gap-2 flex-wrap">
                <div className="text-sm">Industry:</div>
                <select
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    className="h-9 px-2 border border-border bg-white text-sm"
                    data-testid="reports-skill-gap-industry"
                >
                    {industries.map((i) => (
                        <option key={i.slug} value={i.slug}>{i.name}{companyIndustries.includes(i.slug) ? " (yours)" : ""}</option>
                    ))}
                </select>
                <Button variant="outline" className="rounded-none h-9" onClick={load} data-testid="reports-skill-gap-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                </Button>
                <a href="/skill-center" className="text-xs text-ikb hover:underline ml-auto" data-testid="reports-skill-gap-manage">
                    Manage catalog →
                </a>
            </div>
            {loading && <div className="border border-border bg-white p-16 text-center text-sm text-muted-foreground">Building report…</div>}
            {data && !loading && <GapReportBody data={data} />}
        </div>
    );
}

function PeriodPicker({ period, setPeriod }) {
    return (
        <div className="flex items-center gap-1.5 border border-border bg-white px-2 py-1.5" data-testid="reports-period">
            <Filter className="w-3.5 h-3.5 text-muted-foreground" />
            <Input type="date" value={period.from} onChange={(e) => setPeriod({ ...period, from: e.target.value })} className="rounded-none h-7 w-36 border-0 focus-visible:ring-0" data-testid="period-from" />
            <span className="text-xs text-muted-foreground">→</span>
            <Input type="date" value={period.to} onChange={(e) => setPeriod({ ...period, to: e.target.value })} className="rounded-none h-7 w-36 border-0 focus-visible:ring-0" data-testid="period-to" />
        </div>
    );
}

function fetchWithPeriod(url, period) {
    const params = {};
    if (period.from) params.period_from = period.from;
    if (period.to) params.period_to = period.to;
    return api.get(url, { params });
}

function ExecutiveTab({ period }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await fetchWithPeriod("/reports/executive", period); setData(data); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [period]);
    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading executive KPIs…</div>;
    if (!data) return null;
    const k = data.kpis || {};
    return (
        <div className="space-y-5" data-testid="exec-view">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi icon={Users} label="Total hires" value={k.total_hires} tint="ikb" testid="kpi-hires" />
                <Kpi icon={Timer} label="Avg TTH" value={k.avg_time_to_hire_days ? `${k.avg_time_to_hire_days}d` : "—"} testid="kpi-tth" />
                <Kpi icon={TrendingUp} label="Offer accept %" value={k.offer_accept_rate != null ? `${k.offer_accept_rate}%` : "—"} testid="kpi-accept" />
                <Kpi icon={DollarSign} label="Cost / hire" value={k.cost_per_hire != null ? `$${k.cost_per_hire.toLocaleString()}` : "—"} testid="kpi-cph" />
                <Kpi icon={DollarSign} label="Revenue" value={`$${(k.revenue || 0).toLocaleString()}`} tint="emerald" testid="kpi-revenue" />
                <Kpi icon={DollarSign} label="Net" value={`$${(k.net || 0).toLocaleString()}`} tint={k.net >= 0 ? "emerald" : "rose"} testid="kpi-net" />
                <Kpi icon={Users} label="Active pipeline" value={k.active_pipeline} testid="kpi-pipeline" />
                <Kpi icon={Timer} label="No-shows" value={k.no_show_count} tint={k.no_show_count > 0 ? "rose" : "muted"} testid="kpi-noshow" />
            </div>
            {(data.weekly_hires || []).length > 0 && (
                <Card title="Hires by week">
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={data.weekly_hires} margin={{ top: 12, right: 12, bottom: 6, left: 6 }}>
                                <XAxis dataKey="week" stroke="#94a3b8" fontSize={11} />
                                <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                                <Tooltip contentStyle={{ borderRadius: 0, fontSize: 12, backgroundColor: "#0f172a", color: "#fff", border: "none" }} />
                                <Line type="monotone" dataKey="hires" stroke="#003fbf" strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
            )}
        </div>
    );
}

function TableTab({ period, url, columns, testid }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await fetchWithPeriod(url, period); setRows(data?.items || []); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [url, period]);
    useEffect(() => { load(); }, [load]);

    return (
        <div className="border border-border bg-white" data-testid={`reports-${testid}-view`}>
            <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">{rows.length} rows</div>
                <Button variant="outline" onClick={load} disabled={loading} className="rounded-none h-7"><RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /></Button>
            </div>
            {loading ? <div className="p-8 text-sm text-muted-foreground text-center">Loading…</div>
             : rows.length === 0 ? <div className="p-12 text-center text-sm text-muted-foreground">No data in this window</div>
             : (
                <div className="overflow-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-secondary/40 text-[10px] font-mono uppercase tracking-widest">
                            <tr>{columns.map((c) => <th key={c.key} className={`px-3 py-2 ${c.align === "right" ? "text-right" : "text-left"}`}>{c.label}</th>)}</tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {rows.map((r, i) => (
                                <tr key={i} className="hover:bg-secondary/30">
                                    {columns.map((c) => (
                                        <td key={c.key} className={`px-3 py-2 text-xs ${c.align === "right" ? "text-right font-mono" : ""}`}>
                                            {c.format ? c.format(r[c.key]) : (r[c.key] ?? "—")}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function SourceTab({ period }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await fetchWithPeriod("/reports/by-source", period); setRows(data?.items || []); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [period]);
    useEffect(() => { load(); }, [load]);

    const chartData = useMemo(() => rows.slice(0, 8).map((r) => ({ name: r.source, hires: r.hires, applications: r.applications })), [rows]);

    return (
        <div className="space-y-5" data-testid="reports-source-view">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card title="Applications by source" className="lg:col-span-2">
                    <div className="h-64">
                        <ResponsiveContainer>
                            <BarChart data={chartData}>
                                <XAxis dataKey="name" stroke="#94a3b8" fontSize={10} />
                                <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                                <Tooltip contentStyle={{ borderRadius: 0, fontSize: 12, backgroundColor: "#0f172a", color: "#fff", border: "none" }} />
                                <Legend />
                                <Bar dataKey="applications" fill="#94a3b8" />
                                <Bar dataKey="hires" fill="#003fbf" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
                <Card title="Hires split">
                    <div className="h-64">
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie data={chartData} dataKey="hires" nameKey="name" outerRadius={72} label={{ fontSize: 10 }}>
                                    {chartData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                                </Pie>
                                <Tooltip contentStyle={{ borderRadius: 0, fontSize: 12, backgroundColor: "#0f172a", color: "#fff", border: "none" }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
            </div>
            <Card title="Source ROI table">
                {loading ? <div className="p-6 text-sm text-muted-foreground">Loading…</div>
                 : rows.length === 0 ? <div className="p-12 text-center text-sm text-muted-foreground">No source data</div>
                 : (
                    <div className="overflow-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-secondary/40 text-[10px] font-mono uppercase tracking-widest">
                                <tr><th className="px-3 py-2 text-left">Source</th><th className="px-3 py-2 text-right">Applications</th><th className="px-3 py-2 text-right">Hires</th><th className="px-3 py-2 text-right">Conv %</th><th className="px-3 py-2 text-right">Cost</th><th className="px-3 py-2 text-right">Cost / hire</th><th className="px-3 py-2 text-right">Revenue</th><th className="px-3 py-2 text-right">ROI</th></tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {rows.map((r, i) => (
                                    <tr key={i} className="hover:bg-secondary/30">
                                        <td className="px-3 py-2">{r.source}</td>
                                        <td className="px-3 py-2 text-right font-mono text-xs">{r.applications}</td>
                                        <td className="px-3 py-2 text-right font-mono text-xs">{r.hires}</td>
                                        <td className="px-3 py-2 text-right font-mono text-xs">{r.conversion_pct != null ? `${r.conversion_pct}%` : "—"}</td>
                                        <td className="px-3 py-2 text-right font-mono text-xs">${(r.cost || 0).toLocaleString()}</td>
                                        <td className="px-3 py-2 text-right font-mono text-xs">{r.cost_per_hire != null ? `$${r.cost_per_hire.toLocaleString()}` : "—"}</td>
                                        <td className="px-3 py-2 text-right font-mono text-xs">${(r.revenue || 0).toLocaleString()}</td>
                                        <td className={`px-3 py-2 text-right font-mono text-xs ${r.roi > 0 ? "text-emerald-700" : r.roi < 0 ? "text-rose-700" : ""}`}>${(r.roi || 0).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
}

function FunnelTab({ period }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await fetchWithPeriod("/reports/funnel", period); setData(data); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [period]);
    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading funnel…</div>;
    if (!data) return null;

    const maxCount = Math.max(...data.stages.map((s) => s.count), 1);
    return (
        <div className="space-y-5" data-testid="reports-funnel-view">
            <div className="border border-border bg-white p-4">
                <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-3">
                    Full funnel · <span className="text-foreground font-bold">{data.total_applications}</span> applications entered top of funnel
                </div>
                <div className="space-y-2">
                    {data.stages.map((s, i) => {
                        const w = Math.max(6, (s.count / maxCount) * 100);
                        const isBottleneck = i > 0 && s.conversion_from_prev_pct != null && s.conversion_from_prev_pct < 30;
                        return (
                            <div key={s.stage} data-testid={`funnel-stage-${s.stage.toLowerCase()}`}>
                                <div className="flex items-center gap-2 mb-0.5">
                                    <div className="text-xs font-mono uppercase tracking-widest w-24 shrink-0">{s.stage}</div>
                                    <div className="flex-1 relative h-8 bg-secondary/30">
                                        <div className="absolute inset-y-0 left-0 bg-ikb flex items-center px-2 text-white text-xs font-mono" style={{ width: `${w}%` }}>
                                            {s.count}
                                        </div>
                                    </div>
                                    <div className={`text-xs font-mono w-24 text-right shrink-0 ${isBottleneck ? "text-rose-600 font-bold" : "text-muted-foreground"}`}>
                                        {s.conversion_from_prev_pct != null ? `${s.conversion_from_prev_pct}%` : "—"}
                                        {isBottleneck && <span className="ml-1" title="Bottleneck — over 70% drop-off">⚠</span>}
                                    </div>
                                </div>
                                {i > 0 && s.drop_from_prev > 0 && (
                                    <div className="pl-24 text-[10px] font-mono text-muted-foreground">
                                        {s.drop_from_prev} dropped off · {s.conversion_from_top_pct}% of top of funnel
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {(data.loss_reasons || []).length > 0 && (
                <Card title="Top loss reasons">
                    <div className="p-4 space-y-1.5">
                        {data.loss_reasons.map((r) => (
                            <div key={r.reason} className="flex items-center gap-2 text-sm">
                                <div className="flex-1 truncate">{r.reason}</div>
                                <div className="text-xs font-mono text-muted-foreground">{r.count}</div>
                                <div className="w-40 h-2 bg-secondary/40 relative">
                                    <div className="absolute inset-y-0 left-0 bg-rose-500" style={{ width: `${(r.count / data.loss_reasons[0].count) * 100}%` }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// Phase R6 — Advanced Intelligence Reporting tabs
// ═══════════════════════════════════════════════════════════════════════════
function HireEtaTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lookback, setLookback] = useState(180);
    const [onlyActive, setOnlyActive] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/reports/hire-eta", { params: { lookback_days: lookback, only_active: onlyActive ? 1 : 0 } });
            setData(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [lookback, onlyActive]);
    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading hire-ETA…</div>;
    if (!data) return null;
    const items = data.items || [];
    const openJobs = items.filter((r) => r.signal !== "filled");
    const filled = items.filter((r) => r.signal === "filled").length;
    const avgDays = openJobs.length ? Math.round(openJobs.reduce((s, r) => s + (r.days_to_all_hires || 0), 0) / Math.max(1, openJobs.filter((r) => r.days_to_all_hires).length)) : null;
    const atRisk = openJobs.filter((r) => r.signal === "at-risk" || r.signal === "critical").length;

    return (
        <div className="space-y-5" data-testid="reports-hire-eta-view">
            <div className="flex items-center gap-2 flex-wrap">
                <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mr-2">Lookback</div>
                {[90, 180, 365].map((d) => (
                    <button key={d} onClick={() => setLookback(d)} className={`px-2.5 py-1 border text-xs font-mono ${lookback === d ? "border-ikb text-ikb bg-ikb/5" : "border-border"}`} data-testid={`hireeta-lookback-${d}`}>{d}d</button>
                ))}
                <label className="inline-flex items-center gap-1.5 ml-3 text-xs">
                    <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} data-testid="hireeta-only-active" />
                    <span>Active jobs only</span>
                </label>
                <Button variant="outline" onClick={load} className="rounded-none h-8 ml-auto" data-testid="hireeta-refresh"><RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /></Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi icon={Target} label="Open jobs" value={openJobs.length} tint="ikb" testid="hireeta-open" />
                <Kpi icon={Timer} label="Avg ETA" value={avgDays ? `${avgDays}d` : "—"} testid="hireeta-avgeta" />
                <Kpi icon={TrendingUp} label="At risk" value={atRisk} tint={atRisk ? "rose" : "muted"} testid="hireeta-atrisk" />
                <Kpi icon={Users} label="Filled" value={filled} tint="emerald" testid="hireeta-filled" />
            </div>
            <div className="border border-border bg-white overflow-auto" data-testid="hireeta-table">
                {items.length === 0 ? <div className="p-12 text-center text-sm text-muted-foreground">No active jobs to forecast.</div> : (
                    <table className="w-full text-sm">
                        <thead className="bg-secondary/40 text-[10px] font-mono uppercase tracking-widest">
                            <tr>
                                <th className="px-3 py-2 text-left">Job</th>
                                <th className="px-3 py-2 text-right">Openings</th>
                                <th className="px-3 py-2 text-right">Hires</th>
                                <th className="px-3 py-2 text-right">Needed</th>
                                <th className="px-3 py-2 text-right">Apply/day</th>
                                <th className="px-3 py-2 text-right">Avg TTH</th>
                                <th className="px-3 py-2 text-right">ETA (days)</th>
                                <th className="px-3 py-2 text-right">Target date</th>
                                <th className="px-3 py-2 text-left">Signal</th>
                                <th className="px-3 py-2 text-left">Confidence</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {items.map((r) => (
                                <tr key={r.job_id} className="hover:bg-secondary/30" data-testid={`hireeta-row-${r.job_id}`}>
                                    <td className="px-3 py-2 text-xs truncate max-w-[240px]" title={r.title}>{r.title}</td>
                                    <td className="px-3 py-2 text-right font-mono text-xs">{r.openings}</td>
                                    <td className="px-3 py-2 text-right font-mono text-xs">{r.hires_to_date}</td>
                                    <td className="px-3 py-2 text-right font-mono text-xs">{r.hires_needed}</td>
                                    <td className="px-3 py-2 text-right font-mono text-xs">{r.apply_rate_per_day ?? "—"}</td>
                                    <td className="px-3 py-2 text-right font-mono text-xs">{r.avg_tth_days ?? "—"}</td>
                                    <td className="px-3 py-2 text-right font-mono text-xs font-bold">{r.days_to_all_hires ?? "—"}</td>
                                    <td className="px-3 py-2 text-right font-mono text-xs">{r.eta_date || "—"}</td>
                                    <td className="px-3 py-2 text-left"><SignalPill signal={r.signal} /></td>
                                    <td className="px-3 py-2 text-left"><ConfidencePill c={r.confidence} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            {data.baseline && (
                <div className="text-[10px] font-mono text-muted-foreground">
                    Company baseline · avg TTH <b className="text-foreground">{data.baseline.avg_tth_days ?? "—"}d</b> · conversion <b className="text-foreground">{data.baseline.conversion_rate != null ? `${(data.baseline.conversion_rate * 100).toFixed(2)}%` : "—"}</b> · {data.baseline.apps_last_window} apps → {data.baseline.hires_last_window} hires in {lookback}d.
                </div>
            )}
        </div>
    );
}

function SignalPill({ signal }) {
    const map = {
        healthy: "border-emerald-300 bg-emerald-50 text-emerald-700",
        filled: "border-emerald-500 bg-emerald-50 text-emerald-800",
        "at-risk": "border-amber-400 bg-amber-50 text-amber-700",
        critical: "border-rose-400 bg-rose-50 text-rose-700",
        "low-inflow": "border-amber-300 bg-amber-50 text-amber-700",
        "sparse-data": "border-border bg-secondary/50 text-muted-foreground",
        "no-baseline": "border-border bg-secondary/50 text-muted-foreground",
    };
    return <span className={`px-1.5 py-0.5 border text-[10px] font-mono uppercase tracking-widest ${map[signal] || map["sparse-data"]}`}>{(signal || "—").replace("-", " ")}</span>;
}
function ConfidencePill({ c }) {
    const map = { high: "bg-emerald-100 text-emerald-800", medium: "bg-amber-100 text-amber-800", low: "bg-slate-100 text-slate-700" };
    return <span className={`px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-widest ${map[c] || map.low}`}>{c || "low"}</span>;
}

function QtthTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lookback, setLookback] = useState(180);
    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/reports/qtth", { params: { lookback_days: lookback } }); setData(data); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [lookback]);
    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading qTTH…</div>;
    if (!data) return null;

    const trend = (data.trend || []).map((t) => ({ month: t.month, tth: t.avg_tth_days, qtth: t.qtth_days }));
    return (
        <div className="space-y-5" data-testid="reports-qtth-view">
            <div className="flex items-center gap-2 flex-wrap">
                {[90, 180, 365].map((d) => (
                    <button key={d} onClick={() => setLookback(d)} className={`px-2.5 py-1 border text-xs font-mono ${lookback === d ? "border-ikb text-ikb bg-ikb/5" : "border-border"}`} data-testid={`qtth-lookback-${d}`}>{d}d</button>
                ))}
                <div className="ml-3 text-[10px] text-muted-foreground">Time-to-hire divided by placement quality (joined ÷ hires, clamped at 10%).</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi icon={Users} label="Total hires" value={data.total_hires} tint="ikb" testid="qtth-total" />
                <Kpi icon={Timer} label="Avg TTH" value={data.avg_tth_days ? `${data.avg_tth_days}d` : "—"} testid="qtth-tth" />
                <Kpi icon={Sparkles} label="qTTH" value={data.qtth_days ? `${data.qtth_days}d` : "—"} tint={data.qtth_days && data.qtth_days > (data.avg_tth_days || 0) * 1.5 ? "rose" : "emerald"} testid="qtth-qtth" />
                <Kpi icon={TrendingUp} label="Quality" value={data.quality_ratio != null ? `${(data.quality_ratio * 100).toFixed(0)}%` : "—"} testid="qtth-quality" />
            </div>
            {trend.length > 0 && (
                <Card title="Trend · TTH vs qTTH (monthly)">
                    <div className="h-64">
                        <ResponsiveContainer>
                            <LineChart data={trend} margin={{ top: 12, right: 12, bottom: 6, left: 6 }}>
                                <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} />
                                <YAxis stroke="#94a3b8" fontSize={11} />
                                <Tooltip contentStyle={{ borderRadius: 0, fontSize: 12, backgroundColor: "#0f172a", color: "#fff", border: "none" }} />
                                <Legend />
                                <Line type="monotone" dataKey="tth" name="Raw TTH" stroke="#94a3b8" strokeWidth={2} />
                                <Line type="monotone" dataKey="qtth" name="qTTH" stroke="#dc2626" strokeWidth={2} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
            )}
            <Card title="By recruiter">
                {data.by_recruiter?.length === 0 ? <div className="p-6 text-sm text-muted-foreground">No recruiter data.</div> : (
                    <div className="overflow-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-secondary/40 text-[10px] font-mono uppercase tracking-widest">
                                <tr><th className="px-3 py-2 text-left">Recruiter</th><th className="px-3 py-2 text-right">Hires</th><th className="px-3 py-2 text-right">Joined</th><th className="px-3 py-2 text-right">No-shows</th><th className="px-3 py-2 text-right">Quality</th><th className="px-3 py-2 text-right">TTH</th><th className="px-3 py-2 text-right">qTTH</th></tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {data.by_recruiter.map((r) => (
                                    <tr key={r.recruiter_id} className="hover:bg-secondary/30">
                                        <td className="px-3 py-2 text-xs">{r.name}</td>
                                        <td className="px-3 py-2 text-right font-mono text-xs">{r.hires}</td>
                                        <td className="px-3 py-2 text-right font-mono text-xs">{r.joined}</td>
                                        <td className={`px-3 py-2 text-right font-mono text-xs ${r.no_show ? "text-rose-700 font-bold" : ""}`}>{r.no_show}</td>
                                        <td className="px-3 py-2 text-right font-mono text-xs">{(r.quality_ratio * 100).toFixed(0)}%</td>
                                        <td className="px-3 py-2 text-right font-mono text-xs">{r.avg_tth_days ?? "—"}</td>
                                        <td className="px-3 py-2 text-right font-mono text-xs font-bold">{r.qtth_days ?? "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
}

function ClientHealthTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lookback, setLookback] = useState(180);
    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/reports/client-health", { params: { lookback_days: lookback } }); setItems(data?.items || []); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [lookback]);
    useEffect(() => { load(); }, [load]);

    const gradeTint = { A: "bg-emerald-500 text-white", B: "bg-emerald-400 text-white", C: "bg-amber-400 text-white", D: "bg-orange-500 text-white", F: "bg-rose-500 text-white" };

    if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading client health…</div>;
    if (items.length === 0) return <div className="p-12 text-center text-sm text-muted-foreground border border-border bg-white">No clients configured yet.</div>;

    return (
        <div className="space-y-5" data-testid="reports-client-health-view">
            <div className="flex items-center gap-2 flex-wrap">
                {[90, 180, 365].map((d) => (
                    <button key={d} onClick={() => setLookback(d)} className={`px-2.5 py-1 border text-xs font-mono ${lookback === d ? "border-ikb text-ikb bg-ikb/5" : "border-border"}`} data-testid={`ch-lookback-${d}`}>{d}d</button>
                ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {items.map((c) => (
                    <div key={c.client_id} className="border border-border bg-white p-4 space-y-3" data-testid={`ch-card-${c.client_id}`}>
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                                <div className="font-semibold truncate">{c.name}</div>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{c.openings || 0} openings · {c.hires} hires · {c.active_jobs} active jobs</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className={`w-10 h-10 flex items-center justify-center font-black text-lg ${gradeTint[c.grade] || "bg-slate-500 text-white"}`}>{c.grade}</div>
                                <div className="text-right">
                                    <div className="text-2xl font-black font-display text-ikb leading-none">{c.health_score}</div>
                                    <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">/ 100</div>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-4 gap-1.5 text-center">
                            <SubScorePill label="Fill" v={c.sub_scores.fill_rate} />
                            <SubScorePill label="Speed" v={c.sub_scores.cycle_speed} />
                            <SubScorePill label="Flow" v={c.sub_scores.pipeline_flow} />
                            <SubScorePill label="Slack" v={c.sub_scores.feedback_slack} />
                        </div>
                        <div className="text-[10px] font-mono text-muted-foreground border-t border-border pt-2">
                            Avg TTH {c.avg_tth_days ?? "—"}d · {c.apps_last_30d} apps last 30d
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function SubScorePill({ label, v }) {
    const pct = Math.round((v || 0) * 100);
    const tint = pct >= 70 ? "bg-emerald-100 text-emerald-800 border-emerald-300"
              : pct >= 40 ? "bg-amber-100 text-amber-800 border-amber-300"
              : "bg-rose-100 text-rose-800 border-rose-300";
    return (
        <div className={`border ${tint} px-1.5 py-1`}>
            <div className="text-[9px] font-mono uppercase tracking-widest">{label}</div>
            <div className="text-xs font-mono font-bold">{pct}</div>
        </div>
    );
}

function Kpi({ icon: Icon, label, value, tint = "muted", testid }) {
    const tints = {
        ikb: "text-ikb", emerald: "text-emerald-700", rose: "text-rose-700", muted: "text-foreground",
    };
    return (
        <div className="border border-border bg-white p-3 sm:p-4" data-testid={testid}>
            <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</span>
                <Icon className={`w-4 h-4 ${tints[tint] || tints.muted}`} />
            </div>
            <div className={`mt-1 font-display font-black text-xl sm:text-2xl ${tints[tint] || tints.muted}`}>{value ?? "—"}</div>
        </div>
    );
}

function Card({ title, children, className = "" }) {
    return (
        <section className={`border border-border bg-white ${className}`}>
            <div className="px-3 py-2 border-b border-border text-[11px] font-mono uppercase tracking-widest text-muted-foreground">{title}</div>
            <div className="p-4">{children}</div>
        </section>
    );
}
